import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { IconPencil, IconTrash } from '@shared/ui/icons';
import type { ClaimRisk, ValidatedRef } from '@entities/legal/types';
import { ClaimRiskAnnotation } from '../ClaimRiskAnnotation';
import {
  categoryLabel,
  DraftBadge,
  DraftText,
  DraftWrap,
  Empty,
  fmtMoney,
  getBool,
  getNum,
  getStr,
  Grid,
  HitlNote,
  RefChip,
  RiskAction,
  RiskBody,
  RiskCard,
  RiskHead,
  RiskRefsRow,
  Section,
  SeverityBadge,
  SummaryBox,
} from './styles';

const NormsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const RisksHeader = styled.h4`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button<{ $danger?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ theme, $danger }) => ($danger ? theme.colors.status.danger : theme.colors.text.muted)};
  display: flex;
`;

const SourceTag = styled.span<{ $lawyer?: boolean }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  letter-spacing: 0.04em;
  background: ${({ theme, $lawyer }) =>
    $lawyer ? theme.colors.status.warning + '26' : theme.colors.primary + '26'};
  color: ${({ theme, $lawyer }) =>
    $lawyer ? theme.colors.status.warning : theme.colors.primary};
`;

const ComposeControls = styled.div`
  margin-top: 12px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const ComposeStatusText = styled.div<{ $kind: 'error' | 'fallback' | 'ok' }>`
  font-size: 13px;
  color: ${({ theme, $kind }) =>
    $kind === 'error'
      ? theme.colors.status.danger
      : $kind === 'fallback'
        ? theme.colors.status.warning
        : theme.colors.status.success};
`;

const DocxList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const DocxRow = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const DocxMeta = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export type ComposedItem = {
  id: string;
  source_similarity: number | null;
  llm_model: string | null;
  created_at: string;
  source_template_kind: string | null;
};

interface ClaimRisksSectionProps {
  claimId: string;
  summary?: string | null;
  analysisMeta?: Record<string, unknown> | null;
  direction: 'incoming' | 'outgoing';
  risks: ClaimRisk[];
  isProcessing: boolean;
  can_write: boolean;
  selectMode: boolean;
  onEnterSelectMode: () => void;
  selectedRisks: Set<string>;
  onToggleRiskSelection: (id: string) => void;
  onOpenManualRisk: () => void;
  onViewClause: (id: string) => void;
  onEditRisk: (id: string) => void;
  onDeleteRisk: (id: string) => void;
  onComposeResponse: () => void;
  isComposing: boolean;
  composeMsg: { kind: 'error' | 'fallback' | 'ok'; text: string } | null;
  composeFallback: string | null;
  onDismissComposeFallback: () => void;
  composedItems: ComposedItem[];
  onDownloadComposed: (id: string) => void;
  onDeleteComposed: (id: string) => void;
}

export function ClaimRisksSection({
  summary,
  analysisMeta,
  direction,
  risks,
  isProcessing,
  can_write,
  selectMode,
  onEnterSelectMode,
  selectedRisks,
  onToggleRiskSelection,
  onOpenManualRisk,
  onViewClause,
  onEditRisk,
  onDeleteRisk,
  onComposeResponse,
  isComposing,
  composeMsg,
  composeFallback,
  onDismissComposeFallback,
  composedItems,
  onDownloadComposed,
  onDeleteComposed,
}: ClaimRisksSectionProps) {
  const meta = analysisMeta ?? {};
  const isIncoming = direction === 'incoming';

  const claimedAmount = fmtMoney(meta['claimed_amount']);
  const recalculatedAmount = fmtMoney(meta['recalculated_amount']);
  const recalcExplanation = getStr(meta, 'recalc_explanation');
  const responseBasis = getStr(meta, 'response_deadline_basis');
  const limitationExpired = getBool(meta, 'limitation_expired');
  const limitationYears = getNum(meta, 'limitation_years');
  const draftResponse = getStr(meta, 'draft_response');
  const applicableNorms = Array.isArray(meta['applicable_norms'])
    ? (meta['applicable_norms'] as unknown[]).filter((s): s is string => typeof s === 'string')
    : [];

  const criticalCount = risks.filter((r) => r.severity === 'critical').length;
  const majorCount = risks.filter((r) => r.severity === 'major').length;

  return (
    <>
      {summary && (
        <Section>
          <h4>Вывод эксперта</h4>
          <SummaryBox>{summary}</SummaryBox>
        </Section>
      )}

      {(claimedAmount || recalculatedAmount) && (
        <Section>
          <h4>
            {isIncoming
              ? 'Перерасчёт требований (наш контррасчёт)'
              : 'Проверка нашего расчёта'}
          </h4>
          <Grid>
            {claimedAmount && (
              <>
                <dt>{isIncoming ? 'Заявлено контрагентом' : 'Заявлено в нашем проекте'}</dt>
                <dd>{claimedAmount}</dd>
              </>
            )}
            {recalculatedAmount && (
              <>
                <dt>Корректная сумма</dt>
                <dd>
                  <strong>{recalculatedAmount}</strong>
                </dd>
              </>
            )}
          </Grid>
          {recalcExplanation && (
            <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5 }}>
              {recalcExplanation}
            </div>
          )}
        </Section>
      )}

      {isIncoming && (responseBasis || limitationExpired !== null) && (
        <Section>
          <h4>Процессуальные сроки</h4>
          <Grid>
            {responseBasis && (
              <>
                <dt>Основание срока ответа</dt>
                <dd>{responseBasis}</dd>
              </>
            )}
            {limitationExpired !== null && (
              <>
                <dt>Срок исковой давности</dt>
                <dd>
                  {limitationExpired ? (
                    <strong style={{ color: 'var(--osnova-success, #15803d)' }}>
                      Истёк — можно заявить о пропуске
                      {limitationYears ? ` (${limitationYears} лет)` : ''}
                    </strong>
                  ) : (
                    <span>
                      Не истёк
                      {limitationYears ? ` (применимый срок: ${limitationYears} лет)` : ''}
                    </span>
                  )}
                </dd>
              </>
            )}
          </Grid>
        </Section>
      )}

      {applicableNorms.length > 0 && (
        <Section>
          <h4>Применимые нормы</h4>
          <NormsWrap>
            {applicableNorms.map((n, i) => (
              <Badge key={i}>{n}</Badge>
            ))}
          </NormsWrap>
        </Section>
      )}

      <Section>
        <RisksHeader>
          <span>
            {isIncoming ? 'Что нашёл агент' : 'Замечания к нашему проекту'} ({risks.length}
            {(criticalCount > 0 || majorCount > 0) &&
              ` · крит. ${criticalCount}, важн. ${majorCount}`}
            )
          </span>
          <HeaderActions>
            {risks.length > 0 && !selectMode && (
              <Button
                type="button"
                $variant="secondary"
                onClick={onEnterSelectMode}
                disabled={!can_write}
                title={!can_write ? 'Только чтение' : undefined}
              >
                Выбрать риски
              </Button>
            )}
            <Button
              type="button"
              $variant="secondary"
              onClick={onOpenManualRisk}
              disabled={!can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              + Свой риск
            </Button>
          </HeaderActions>
        </RisksHeader>

        {risks.length === 0 ? (
          isProcessing ? (
            <Empty>Анализ идёт — замечания появятся здесь по мере готовности.</Empty>
          ) : (
            <Empty>Замечаний нет.</Empty>
          )
        ) : (
          risks.map((r) => (
            <RiskCard key={r.id}>
              <RiskHead>
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={selectedRisks.has(r.id)}
                    onChange={() => onToggleRiskSelection(r.id)}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                    aria-label={`Выбрать риск ${r.description.slice(0, 40)}`}
                  />
                )}
                <SeverityBadge $sev={r.severity}>{r.severity}</SeverityBadge>
                <span>{categoryLabel(direction, r.category)}</span>
                {r.source === 'manual' && <SourceTag>ДОБАВЛЕН ЮРИСТОМ</SourceTag>}
                {r.source === 'lawyer_corrected' && (
                  <SourceTag $lawyer>ПРАВЛЕН ЮРИСТОМ</SourceTag>
                )}
                {r.confidence != null && (
                  <span style={{ marginLeft: 'auto' }}>
                    уверенность {(r.confidence * 100).toFixed(0)}%
                  </span>
                )}
                <span style={{ display: 'flex', gap: 2 }}>
                  <IconButton
                    type="button"
                    title="Редактировать"
                    onClick={() => onEditRisk(r.id)}
                  >
                    <IconPencil size={14} />
                  </IconButton>
                  <IconButton
                    type="button"
                    title="Удалить риск"
                    $danger
                    onClick={() => onDeleteRisk(r.id)}
                  >
                    <IconTrash size={14} />
                  </IconButton>
                </span>
              </RiskHead>
              <RiskBody
                role="button"
                tabIndex={0}
                onClick={() => (selectMode ? onToggleRiskSelection(r.id) : onViewClause(r.id))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (selectMode) onToggleRiskSelection(r.id);
                    else onViewClause(r.id);
                  }
                }}
                title={
                  selectMode
                    ? 'Кликни, чтобы добавить/убрать из выборки'
                    : r.clause_quote
                      ? 'Кликни, чтобы увидеть исходный текст пункта'
                      : 'Кликни, чтобы увидеть подробности риска'
                }
                style={{ cursor: 'pointer' }}
              >
                {r.description}
              </RiskBody>
              {r.proposed_action && (
                <RiskAction>
                  <strong>
                    {isIncoming ? 'Что использовать в ответе' : 'Что поправить в проекте'}
                  </strong>
                  {r.proposed_action}
                </RiskAction>
              )}
              {Array.isArray(r.refs) && r.refs.length > 0 && (
                <RiskRefsRow>
                  {(r.refs as ValidatedRef[]).map((ref, i) => (
                    <RefChip
                      key={i}
                      $found={ref.found}
                      title={
                        ref.found
                          ? `Подтверждено: ${ref.source_title ?? ''}`
                          : 'Не подтверждено в KB — требует ручной сверки'
                      }
                    >
                      {ref.citation}
                      {ref.found ? ' ✓' : ' ?'}
                    </RefChip>
                  ))}
                </RiskRefsRow>
              )}
              <ClaimRiskAnnotation risk={r} />
            </RiskCard>
          ))
        )}
      </Section>

      {isIncoming && draftResponse && (
        <Section>
          <h4>Проект ответа от АО «САТКОМ»</h4>
          <DraftWrap>
            <DraftBadge>Черновик · согласовать с юристом</DraftBadge>
            <DraftText>{draftResponse}</DraftText>
            <HitlNote>
              (Human-in-the-loop): любой исходящий ответ контрагенту согласует юрист. Это автоматически
              сгенерированный проект — отредактируйте перед отправкой.
            </HitlNote>
            <ComposeControls>
              <Button
                type="button"
                onClick={onComposeResponse}
                disabled={isComposing || isProcessing || !can_write}
                title={
                  !can_write
                    ? 'Только чтение'
                    : 'Собрать DOCX-ответ с шапкой САТКОМ по образцу из «Шаблоны ответов» в БЗ'
                }
              >
                {isComposing ? 'Составляю…' : 'Составить ответ (DOCX)'}
              </Button>
              {composeMsg && (
                <ComposeStatusText role={composeMsg.kind === 'error' ? 'alert' : 'status'} $kind={composeMsg.kind}>
                  {composeMsg.text}
                </ComposeStatusText>
              )}
            </ComposeControls>
          </DraftWrap>
        </Section>
      )}

      {composeFallback && (
        <Section>
          <h4>DOCX по шаблону</h4>
          <FallbackBanner title="Не удалось собрать DOCX по шаблону." onDismiss={onDismissComposeFallback}>
            {composeFallback}
          </FallbackBanner>
        </Section>
      )}

      {composedItems.length > 0 && (
        <Section>
          <h4>Черновики DOCX ({composedItems.length})</h4>
          <DocxList>
            {composedItems.map((it) => (
              <DocxRow key={it.id}>
                <DocxMeta>
                  <Link to={`/agents/statut/documents/${it.id}`}>
                    {new Date(it.created_at).toLocaleString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Link>
                  {/* Сходство с шаблоном около нуля юристу ничего не говорит —
                      показываем, когда оно заметно. */}
                  {it.source_similarity !== null && it.source_similarity >= 0.05 && (
                    <span style={{ marginLeft: 8 }}>· похожесть {(it.source_similarity * 100).toFixed(0)}%</span>
                  )}
                  {it.llm_model && <span style={{ marginLeft: 8 }}>· {it.llm_model}</span>}
                </DocxMeta>
                <Button type="button" $size="sm" $variant="secondary" onClick={() => onDownloadComposed(it.id)}>
                  Скачать
                </Button>
                <Button
                  type="button"
                  $size="sm"
                  $variant="ghost"
                  onClick={() => onDeleteComposed(it.id)}
                  disabled={!can_write}
                  title={!can_write ? 'Только чтение' : 'Удалить этот сгенерированный DOCX'}
                >
                  Удалить
                </Button>
              </DocxRow>
            ))}
          </DocxList>
        </Section>
      )}
    </>
  );
}
